// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import Channels from '@app/features/channel/state/Channels';
import GuildMembers from '@app/features/member/state/GuildMembers';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {deleteTask, setArchived, type UpdateTaskInput, updateTask} from '@app/features/tasks/commands/TaskCommands';
import {TaskComments} from '@app/features/tasks/components/TaskComments';
import styles from '@app/features/tasks/components/TaskEditDialog.module.css';
import {assigneesOf, type Task, type TaskStatus} from '@app/features/tasks/state/Tasks';
import {
	ARCHIVE_DESCRIPTOR,
	DELETE_TODO_DESCRIPTOR,
	DOING_COLUMN_DESCRIPTOR,
	DONE_DESCRIPTOR,
	INPUT_COLUMN_DESCRIPTOR,
	QA_COLUMN_DESCRIPTOR,
	TEAM_DESCRIPTOR,
	TODO_COLUMN_DESCRIPTOR,
} from '@app/features/tasks/utils/TaskMessageDescriptors';
import {Avatar} from '@app/features/ui/components/Avatar';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {
	ArchiveIcon,
	ArrowSquareOutIcon,
	CheckIcon,
	DotsThreeIcon,
	MagnifyingGlassIcon,
	PlusIcon,
	TrashIcon,
	XIcon,
} from '@phosphor-icons/react';
import clsx from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useId, useRef, useState} from 'react';

const TODO = msg({message: 'To-do', comment: 'Small label at the top of an open to-do.'});
const SAVED = msg({message: 'Saved', comment: 'Shown after a change to a to-do is saved.'});
const SAVING = msg({message: 'Saving…', comment: 'Shown while a change to a to-do is being saved.'});
const TITLE_PLACEHOLDER = msg({message: 'What needs doing?', comment: 'Placeholder of a to-do title.'});
const NOTES_PLACEHOLDER = msg({message: 'Add notes…', comment: 'Placeholder of a to-do notes box.'});
// No {placeholders}: messages added after the last catalog compile are shown as written.
const CREATED_BY = msg({message: 'Created by', comment: 'Followed by the name of who made the to-do.'});
const PEOPLE = msg({message: 'People', comment: 'Row label: who the to-do is for.'});
const NOBODY = msg({message: 'Add people', comment: 'Shown when a to-do has no people.'});
const YOU = msg({message: 'You', comment: 'Stands for the current user in a list of names.'});
const ADD_PERSON = msg({message: 'Add or remove people', comment: 'Button that opens the people picker.'});
const DUE = msg({message: 'Due', comment: 'Row label: due date.'});
const ADD_DATE = msg({message: 'Add date', comment: 'Shown when a to-do has no due date.'});
const CLEAR_DATE = msg({message: 'Clear date', comment: 'Removes the due date.'});
const VISIBLE_TO = msg({message: 'Visible to', comment: 'Row label: team or only me.'});
const ONLY_ME = msg({message: 'Only me', comment: 'Option: a personal to-do only you can see.'});
const COLUMN = msg({message: 'Column', comment: 'Accessible label of the board column picker.'});
const FIND_PERSON = msg({message: 'Find a person', comment: 'Search box in the people picker.'});
const NOBODY_FOUND = msg({message: 'Nobody matches that name.', comment: 'Empty people search.'});
const DONE_PICKING = msg({message: 'Done', comment: 'Closes the people picker.'});
const MORE = msg({message: 'More', comment: 'Opens the to-do menu.'});
const OPEN_MESSAGE = msg({message: 'Open message in chat', comment: 'Jumps to the chat message the to-do came from.'});
const CLOSE = msg({message: 'Close', comment: 'Closes the to-do.'});

export const STATUS_COLORS: Record<TaskStatus, string> = {
	todo: '#8f8b82',
	input: '#d97706',
	doing: '#e5a50a',
	qa: '#7c6cf2',
	done: '#2fa66b',
};

function formatDue(due: string, locale: string): string {
	const [y, m, d] = due.split('-').map(Number);
	return new Date(y, m - 1, d).toLocaleDateString(locale, {weekday: 'short', month: 'short', day: 'numeric'});
}

/** A text box that grows with its text and saves when you leave it (or press Enter, for single-line ones). */
function AutoText({
	value,
	onSave,
	className,
	placeholder,
	singleLine = false,
	label,
}: {
	value: string;
	onSave: (next: string) => void;
	className: string;
	placeholder: string;
	singleLine?: boolean;
	label: string;
}) {
	const [draft, setDraft] = useState(value);
	const ref = useRef<HTMLTextAreaElement | null>(null);
	useEffect(() => setDraft(value), [value]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: re-measure whenever the text changes
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const fit = () => {
			el.style.height = 'auto';
			// Hidden (dialog not shown yet) measures 0: leave the CSS minimum in place then.
			if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight}px`;
		};
		fit();
		const frame = requestAnimationFrame(fit);
		return () => cancelAnimationFrame(frame);
	}, [draft]);
	return (
		<textarea
			ref={ref}
			className={className}
			rows={1}
			value={draft}
			placeholder={placeholder}
			aria-label={label}
			onChange={(event) => setDraft(singleLine ? event.target.value.replace(/\n/g, ' ') : event.target.value)}
			onBlur={() => {
				if (draft !== value) onSave(draft);
			}}
			onKeyDown={(event) => {
				if (singleLine && event.key === 'Enter') {
					event.preventDefault();
					event.currentTarget.blur();
				}
				if (event.key === 'Escape') {
					setDraft(value);
					event.stopPropagation();
				}
			}}
			data-flx="tasks.task-edit-dialog.auto-text"
		/>
	);
}

/** An open to-do: title, column, people, due date, notes and comments. Every change saves straight away. */
export const TaskEditDialog = observer(function TaskEditDialog({task, onClose}: {task: Task; onClose: () => void}) {
	const {i18n} = useLingui();
	const dialogRef = useRef<HTMLDialogElement | null>(null);
	const titleId = useId();
	const [saving, setSaving] = useState<'idle' | 'saving' | 'saved'>('idle');
	const [picking, setPicking] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const [search, setSearch] = useState('');
	const me = Users.currentUserId;
	const isCreator = me === task.creator_id;
	const people = assigneesOf(task);
	const channel = task.channel_id ? Channels.getChannel(task.channel_id) : undefined;

	const columns: Array<[TaskStatus, string]> = [
		['todo', i18n._(TODO_COLUMN_DESCRIPTOR)],
		['input', i18n._(INPUT_COLUMN_DESCRIPTOR)],
		['doing', i18n._(DOING_COLUMN_DESCRIPTOR)],
		['qa', i18n._(QA_COLUMN_DESCRIPTOR)],
		['done', i18n._(DONE_DESCRIPTOR)],
	];
	const columnLabel = columns.find(([key]) => key === task.status)?.[1] ?? '';

	useEffect(() => {
		const dialog = dialogRef.current;
		if (dialog && !dialog.open) dialog.showModal();
	}, []);

	// The app only keeps members it has seen; ask for the whole community once.
	useEffect(() => {
		void GuildMembers.fetchMembers(task.guild_id, {query: '', limit: 0, presences: false}).catch(() => {});
	}, [task.guild_id]);

	const save = (changes: UpdateTaskInput) => {
		setSaving('saving');
		void updateTask(task, changes).then(() => setSaving('saved'));
	};

	const memberName = (id: string) => {
		const member = GuildMembers.getMembers(task.guild_id).find((m) => m.user.id === id);
		const user = Users.getUser(id);
		return member?.nick || user?.globalName || user?.username || '…';
	};
	const nameOf = (id: string) => (id === me ? i18n._(YOU) : memberName(id));

	const members = (() => {
		const list = GuildMembers.getMembers(task.guild_id).map((member) => ({
			id: member.user.id,
			name: member.nick || member.user.globalName || member.user.username,
		}));
		for (const id of people) {
			if (!list.some((person) => person.id === id)) list.push({id, name: memberName(id)});
		}
		return list.sort((a, b) => a.name.localeCompare(b.name));
	})();
	const fold = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
	const query = fold(search.trim());
	const shown = members.filter((person) => !query || fold(person.name).includes(query));
	const togglePerson = (id: string) =>
		save({assignee_ids: people.includes(id) ? people.filter((x) => x !== id) : [...people, id]});

	const openMessage = () => {
		if (!task.channel_id) return;
		onClose();
		RouterUtils.transitionTo(
			task.message_id
				? Routes.channelMessage(task.guild_id, task.channel_id, task.message_id)
				: Routes.guildChannel(task.guild_id, task.channel_id),
		);
	};

	return (
		<dialog
			ref={dialogRef}
			className={styles.dialog}
			aria-labelledby={titleId}
			onCancel={(event) => {
				event.preventDefault();
				if (picking) setPicking(false);
				else if (menuOpen) setMenuOpen(false);
				else onClose();
			}}
			onClick={(event) => {
				if (event.target === dialogRef.current) onClose();
			}}
			onKeyDown={(event) => event.stopPropagation()}
			data-flx="tasks.task-edit-dialog.dialog"
		>
			<div className={styles.layout} data-flx="tasks.task-edit-dialog.layout">
				<div className={styles.main} data-flx="tasks.task-edit-dialog.main">
					<header className={styles.top} data-flx="tasks.task-edit-dialog.top">
						<span className={styles.kicker} data-flx="tasks.task-edit-dialog.kicker">
							{i18n._(TODO)}
						</span>
						<span className={styles.saved} aria-live="polite" data-flx="tasks.task-edit-dialog.saved">
							{saving === 'saving' && i18n._(SAVING)}
							{saving === 'saved' && (
								<>
									<CheckIcon size={12} weight="bold" aria-hidden="true" data-flx="tasks.task-edit-dialog.saved-icon" />
									{i18n._(SAVED)}
								</>
							)}
						</span>
						<div className={styles.menuWrap} data-flx="tasks.task-edit-dialog.menu-wrap">
							<button
								type="button"
								className={styles.iconButton}
								aria-label={i18n._(MORE)}
								aria-expanded={menuOpen}
								onClick={() => setMenuOpen((open) => !open)}
								data-flx="tasks.task-edit-dialog.more.button"
							>
								<DotsThreeIcon size={20} weight="bold" aria-hidden="true" data-flx="tasks.task-edit-dialog.more-icon" />
							</button>
							{menuOpen && (
								<div className={styles.menu} role="menu" data-flx="tasks.task-edit-dialog.menu">
									{task.channel_id && (
										<button
											type="button"
											role="menuitem"
											className={styles.menuItem}
											onClick={openMessage}
											data-flx="tasks.task-edit-dialog.menu-open-message.button"
										>
											<ArrowSquareOutIcon size={16} aria-hidden="true" data-flx="tasks.task-edit-dialog.menu-open-icon" />
											{i18n._(OPEN_MESSAGE)}
										</button>
									)}
									<button
										type="button"
										role="menuitem"
										className={styles.menuItem}
										onClick={() => {
											void setArchived(task, true);
											onClose();
										}}
										data-flx="tasks.task-edit-dialog.menu-archive.button"
									>
										<ArchiveIcon size={16} aria-hidden="true" data-flx="tasks.task-edit-dialog.menu-archive-icon" />
										{i18n._(ARCHIVE_DESCRIPTOR)}
									</button>
									<button
										type="button"
										role="menuitem"
										className={clsx(styles.menuItem, styles.menuDanger)}
										onClick={() => {
											void deleteTask(task);
											onClose();
										}}
										data-flx="tasks.task-edit-dialog.menu-delete.button"
									>
										<TrashIcon size={16} aria-hidden="true" data-flx="tasks.task-edit-dialog.menu-trash-icon" />
										{i18n._(DELETE_TODO_DESCRIPTOR)}
									</button>
								</div>
							)}
						</div>
						<button
							type="button"
							className={clsx(styles.iconButton, styles.closeMain)}
							aria-label={i18n._(CLOSE)}
							onClick={onClose}
							data-flx="tasks.task-edit-dialog.close-main.button"
						>
							<XIcon size={18} aria-hidden="true" data-flx="tasks.task-edit-dialog.close-icon" />
						</button>
					</header>

					<div className={styles.chips} data-flx="tasks.task-edit-dialog.chips">
						<label className={styles.chip} data-flx="tasks.task-edit-dialog.column-chip">
							<span
								className={styles.dot}
								style={{background: STATUS_COLORS[task.status]}}
								data-flx="tasks.task-edit-dialog.dot"
							/>
							{columnLabel}
							<span className={styles.caret} aria-hidden="true" data-flx="tasks.task-edit-dialog.caret">
								▾
							</span>
							<select
								className={styles.chipSelect}
								aria-label={i18n._(COLUMN)}
								value={task.status}
								onChange={(event) => save({status: event.target.value as TaskStatus})}
								data-flx="tasks.task-edit-dialog.column-select"
							>
								{columns.map(([key, label]) => (
									<option key={key} value={key} data-flx="tasks.task-edit-dialog.column-option">
										{label}
									</option>
								))}
							</select>
						</label>
						{channel && (
							<button
								type="button"
								className={styles.channelLink}
								onClick={openMessage}
								data-flx="tasks.task-edit-dialog.channel-link.button"
							>
								# {channel.name}
							</button>
						)}
					</div>

					<h2 id={titleId} className={styles.titleWrap} data-flx="tasks.task-edit-dialog.title-wrap">
						<AutoText
							value={task.title}
							singleLine
							className={styles.title}
							placeholder={i18n._(TITLE_PLACEHOLDER)}
							label={i18n._(TITLE_PLACEHOLDER)}
							onSave={(next) => {
								const title = next.trim();
								if (title) save({title});
							}}
						/>
					</h2>
					<p className={styles.byline} data-flx="tasks.task-edit-dialog.byline">
						{i18n._(CREATED_BY)} {nameOf(task.creator_id)}
					</p>

					<dl className={styles.props} data-flx="tasks.task-edit-dialog.props">
						{!task.private && (
							<div className={styles.prop} data-flx="tasks.task-edit-dialog.people-row">
								<dt className={styles.propLabel} data-flx="tasks.task-edit-dialog.people-label">
									{i18n._(PEOPLE)}
								</dt>
								<dd className={styles.propValue} data-flx="tasks.task-edit-dialog.people-value">
									<button
										type="button"
										className={styles.peopleButton}
										onClick={() => setPicking(true)}
										aria-label={i18n._(ADD_PERSON)}
										data-flx="tasks.task-edit-dialog.people.set-picking.button"
									>
										{people.length > 0 ? (
											<>
												<span className={styles.stack} data-flx="tasks.task-edit-dialog.stack">
													{people.slice(0, 4).map((id) => {
														const user = Users.getUser(id);
														return user ? (
															<span key={id} className={styles.stackItem} data-flx="tasks.task-edit-dialog.stack-item">
																<Avatar user={user} size={24} data-flx="tasks.task-edit-dialog.stack-avatar" />
															</span>
														) : null;
													})}
												</span>
												<span className={styles.names} data-flx="tasks.task-edit-dialog.names">
													{people.map(nameOf).join(', ')}
												</span>
											</>
										) : (
											<span className={styles.muted} data-flx="tasks.task-edit-dialog.nobody">
												{i18n._(NOBODY)}
											</span>
										)}
										<span className={styles.plus} aria-hidden="true" data-flx="tasks.task-edit-dialog.plus">
											<PlusIcon size={14} data-flx="tasks.task-edit-dialog.plus-icon" />
										</span>
									</button>
									{picking && (
										<div className={styles.picker} data-flx="tasks.task-edit-dialog.picker">
											<span className={styles.search} data-flx="tasks.task-edit-dialog.search">
												<MagnifyingGlassIcon size={16} aria-hidden="true" data-flx="tasks.task-edit-dialog.search-icon" />
												<input
													// biome-ignore lint/a11y/noAutofocus: opened on purpose to pick people
													autoFocus
													type="search"
													className={styles.searchInput}
													placeholder={i18n._(FIND_PERSON)}
													aria-label={i18n._(FIND_PERSON)}
													value={search}
													onChange={(event) => setSearch(event.target.value)}
													data-flx="tasks.task-edit-dialog.search-input"
												/>
											</span>
											<ul className={styles.personList} data-flx="tasks.task-edit-dialog.person-list">
												{shown.length === 0 && (
													<li className={styles.muted} data-flx="tasks.task-edit-dialog.nobody-found">
														{i18n._(NOBODY_FOUND)}
													</li>
												)}
												{shown.map((person) => {
													const on = people.includes(person.id);
													const user = Users.getUser(person.id);
													return (
														<li key={person.id} data-flx="tasks.task-edit-dialog.person-item">
															<button
																type="button"
																className={clsx(styles.person, on && styles.personOn)}
																aria-pressed={on}
																onClick={() => togglePerson(person.id)}
																data-flx="tasks.task-edit-dialog.person.toggle-person.button"
															>
																{user ? (
																	<Avatar user={user} size={24} data-flx="tasks.task-edit-dialog.avatar" />
																) : (
																	<span className={styles.avatarBlank} data-flx="tasks.task-edit-dialog.avatar-blank" />
																)}
																<span className={styles.personName} data-flx="tasks.task-edit-dialog.person-name">
																	{person.name}
																</span>
																{on && (
																	<CheckIcon size={16} weight="bold" aria-hidden="true" data-flx="tasks.task-edit-dialog.check-icon" />
																)}
															</button>
														</li>
													);
												})}
											</ul>
											<button
												type="button"
												className={styles.pickerDone}
												onClick={() => {
													setPicking(false);
													setSearch('');
												}}
												data-flx="tasks.task-edit-dialog.picker-done.button"
											>
												{i18n._(DONE_PICKING)}
											</button>
										</div>
									)}
								</dd>
							</div>
						)}
						<div className={styles.prop} data-flx="tasks.task-edit-dialog.due-row">
							<dt className={styles.propLabel} data-flx="tasks.task-edit-dialog.due-label">
								{i18n._(DUE)}
							</dt>
							<dd className={styles.propValue} data-flx="tasks.task-edit-dialog.due-value">
								<label className={styles.dateChip} data-flx="tasks.task-edit-dialog.date-chip">
									<span className={task.due_date ? undefined : styles.muted} data-flx="tasks.task-edit-dialog.date-text">
										{task.due_date ? formatDue(task.due_date, i18n.locale) : i18n._(ADD_DATE)}
									</span>
									<input
										type="date"
										className={styles.dateInput}
										value={task.due_date ?? ''}
										aria-label={i18n._(DUE)}
										onChange={(event) => save({due_date: event.target.value || null})}
										data-flx="tasks.task-edit-dialog.date-input"
									/>
								</label>
								{task.due_date && (
									<button
										type="button"
										className={styles.iconButton}
										aria-label={i18n._(CLEAR_DATE)}
										title={i18n._(CLEAR_DATE)}
										onClick={() => save({due_date: null})}
										data-flx="tasks.task-edit-dialog.clear-date.button"
									>
										<XIcon size={14} aria-hidden="true" data-flx="tasks.task-edit-dialog.clear-icon" />
									</button>
								)}
							</dd>
						</div>
						<div className={styles.prop} data-flx="tasks.task-edit-dialog.visible-row">
							<dt className={styles.propLabel} data-flx="tasks.task-edit-dialog.visible-label">
								{i18n._(VISIBLE_TO)}
							</dt>
							<dd className={styles.propValue} data-flx="tasks.task-edit-dialog.visible-value">
								{isCreator ? (
									<label className={styles.chip} data-flx="tasks.task-edit-dialog.visible-chip">
										{i18n._(task.private ? ONLY_ME : TEAM_DESCRIPTOR)}
										<span className={styles.caret} aria-hidden="true" data-flx="tasks.task-edit-dialog.caret--2">
											▾
										</span>
										<select
											className={styles.chipSelect}
											aria-label={i18n._(VISIBLE_TO)}
											value={task.private ? 'me' : 'team'}
											onChange={(event) => save({private: event.target.value === 'me'})}
											data-flx="tasks.task-edit-dialog.visible-select"
										>
											<option value="team" data-flx="tasks.task-edit-dialog.visible-team">
												{i18n._(TEAM_DESCRIPTOR)}
											</option>
											<option value="me" data-flx="tasks.task-edit-dialog.visible-me">
												{i18n._(ONLY_ME)}
											</option>
										</select>
									</label>
								) : (
									<span data-flx="tasks.task-edit-dialog.visible-text">{i18n._(TEAM_DESCRIPTOR)}</span>
								)}
							</dd>
						</div>
					</dl>

					<AutoText
						value={task.notes ?? ''}
						className={styles.notes}
						placeholder={i18n._(NOTES_PLACEHOLDER)}
						label={i18n._(NOTES_PLACEHOLDER)}
						onSave={(next) => save({notes: next.trim() || null})}
					/>
				</div>

				<aside className={styles.side} data-flx="tasks.task-edit-dialog.side">
					<button
						type="button"
						className={clsx(styles.iconButton, styles.closeSide)}
						aria-label={i18n._(CLOSE)}
						onClick={onClose}
						data-flx="tasks.task-edit-dialog.close-side.button"
					>
						<XIcon size={18} aria-hidden="true" data-flx="tasks.task-edit-dialog.close-side-icon" />
					</button>
					<TaskComments task={task} data-flx="tasks.task-edit-dialog.comments" />
				</aside>
			</div>
		</dialog>
	);
});
